import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  FlatList,
  useColorScheme,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const RankingsScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  const [activeTab, setActiveTab] = useState('总体');
  const [timeRange, setTimeRange] = useState('周');
  const [loading, setLoading] = useState(false);

  // 模拟排行榜数据获取
  useEffect(() => {
    const fetchRankings = async () => {
      setLoading(true);
      // 这里应该是从API获取数据的逻辑
      // 模拟网络请求延迟
      await new Promise(resolve => setTimeout(resolve, 500));
      setLoading(false);
    };

    fetchRankings();
  }, [activeTab, timeRange]);

  // 模拟数据 - 用户头像
  const defaultAvatar = 'https://via.placeholder.com/50';
  
  // 模拟数据 - 用户排名
  const currentUserRank = {
    总体: { rank: 12, score: 850, change: 3 },
    运动: { rank: 8, score: 920, change: 5 },
    饮食: { rank: 15, score: 780, change: -2 },
    睡眠: { rank: 25, score: 720, change: 1 },
    冥想: { rank: 7, score: 890, change: 0 },
  };

  // 模拟数据 - 好友排行榜
  const friendRankings = [
    {
      id: '1',
      name: '李晓华',
      avatar: defaultAvatar,
      scores: {
        总体: { score: 950, rank: 5, change: 2 },
        运动: { score: 980, rank: 3, change: 0 },
        饮食: { score: 910, rank: 7, change: 1 },
        睡眠: { score: 850, rank: 12, change: 3 },
        冥想: { score: 920, rank: 6, change: -1 },
      },
      isCurrentUser: false,
    },
    {
      id: '2',
      name: '王明',
      avatar: defaultAvatar,
      scores: {
        总体: { score: 920, rank: 7, change: -1 },
        运动: { score: 890, rank: 10, change: 0 },
        饮食: { score: 960, rank: 4, change: 2 },
        睡眠: { score: 920, rank: 7, change: 1 },
        冥想: { score: 850, rank: 15, change: -3 },
      },
      isCurrentUser: false,
    },
    {
      id: '3',
      name: '张伟',
      avatar: defaultAvatar,
      scores: {
        总体: { score: 880, rank: 9, change: 1 },
        运动: { score: 850, rank: 12, change: -2 },
        饮食: { score: 920, rank: 6, change: 1 },
        睡眠: { score: 880, rank: 10, change: 0 },
        冥想: { score: 900, rank: 8, change: 2 },
      },
      isCurrentUser: false,
    },
    {
      id: '4',
      name: '陈佳',
      avatar: defaultAvatar,
      scores: {
        总体: { score: 820, rank: 15, change: -3 },
        运动: { score: 800, rank: 18, change: -5 },
        饮食: { score: 850, rank: 12, change: 1 },
        睡眠: { score: 900, rank: 8, change: 3 },
        冥想: { score: 800, rank: 20, change: -2 },
      },
      isCurrentUser: false,
    },
    {
      id: '5',
      name: '我',
      avatar: defaultAvatar,
      scores: {
        总体: { score: 850, rank: 12, change: 3 },
        运动: { score: 920, rank: 8, change: 5 },
        饮食: { score: 780, rank: 15, change: -2 },
        睡眠: { score: 720, rank: 25, change: 1 },
        冥想: { score: 890, rank: 7, change: 0 },
      },
      isCurrentUser: true,
    },
    {
      id: '6',
      name: '刘强',
      avatar: defaultAvatar,
      scores: {
        总体: { score: 790, rank: 18, change: 0 },
        运动: { score: 760, rank: 22, change: -1 },
        饮食: { score: 820, rank: 14, change: 2 },
        睡眠: { score: 830, rank: 13, change: 0 },
        冥想: { score: 780, rank: 21, change: -4 },
      },
      isCurrentUser: false,
    },
  ];

  // 排序好友排行数据
  const sortedRankings = [...friendRankings].sort((a, b) => {
    return a.scores[activeTab].rank - b.scores[activeTab].rank;
  });

  // 渲染排名变化图标和文本
  const renderRankChange = (change) => {
    if (change > 0) {
      return (
        <View style={styles.rankChangeUp}>
          <Ionicons name="arrow-up" size={12} color="#10b981" />
          <Text style={styles.rankChangeTextUp}>{change}</Text>
        </View>
      );
    } else if (change < 0) {
      return (
        <View style={styles.rankChangeDown}>
          <Ionicons name="arrow-down" size={12} color="#ef4444" />
          <Text style={styles.rankChangeTextDown}>{Math.abs(change)}</Text>
        </View>
      );
    } else {
      return (
        <View style={styles.rankChangeSame}>
          <Ionicons name="remove" size={12} color="#9ca3af" />
          <Text style={styles.rankChangeTextSame}>0</Text>
        </View>
      );
    }
  };

  // 渲染排名项
  const renderRankItem = ({ item, index }) => {
    const isCurrentUser = item.isCurrentUser;
    const rankData = item.scores[activeTab];
    
    return (
      <View 
        style={[
          styles.rankItem,
          isDarkMode ? styles.rankItemDark : styles.rankItemLight,
          isCurrentUser && (isDarkMode ? styles.currentUserDark : styles.currentUserLight)
        ]}
      >
        <View style={styles.rankLeft}>
          <View style={styles.rankNumberContainer}>
            <Text style={[
              styles.rankNumber,
              isDarkMode && styles.textLight,
              index < 3 && styles[`top${index + 1}RankText`]
            ]}>
              {rankData.rank}
            </Text>
          </View>
          <Image 
            source={{ uri: item.avatar }} 
            style={styles.avatar}
          />
          <Text style={[
            styles.userName,
            isDarkMode && styles.textLight,
            isCurrentUser && styles.currentUserText
          ]}>
            {item.name}
          </Text>
        </View>
        
        <View style={styles.rankRight}>
          <Text style={[
            styles.scoreText,
            isDarkMode && styles.textLight
          ]}>
            {rankData.score}
          </Text>
          {renderRankChange(rankData.change)}
        </View>
      </View>
    );
  };

  // 渲染顶部类别选择标签
  const renderTabs = () => {
    const tabs = ['总体', '运动', '饮食', '睡眠', '冥想'];
    
    return (
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsContainer}
      >
        {tabs.map(tab => (
          <TouchableOpacity
            key={tab}
            style={[
              styles.tab,
              activeTab === tab && styles.activeTab,
              activeTab === tab && isDarkMode && styles.activeTabDark
            ]}
            onPress={() => setActiveTab(tab)}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === tab && styles.activeTabText,
                isDarkMode && styles.textLight,
                activeTab === tab && isDarkMode && styles.activeTabTextDark
              ]}
            >
              {tab}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    );
  };

  // 渲染时间范围选择
  const renderTimeRanges = () => {
    const ranges = ['日', '周', '月', '年'];
    
    return (
      <View style={styles.timeRangesContainer}>
        {ranges.map(range => (
          <TouchableOpacity
            key={range}
            style={[
              styles.timeRange,
              timeRange === range && styles.activeTimeRange,
              timeRange === range && isDarkMode && styles.activeTimeRangeDark
            ]}
            onPress={() => setTimeRange(range)}
          >
            <Text
              style={[
                styles.timeRangeText,
                timeRange === range && styles.activeTimeRangeText,
                isDarkMode && styles.textLight,
                timeRange === range && isDarkMode && styles.activeTimeRangeTextDark
              ]}
            >
              {range}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  return (
    <View style={[
      styles.container,
      isDarkMode ? styles.containerDark : styles.containerLight
    ]}>
      {/* 标题和类别选择 */}
      <View style={styles.header}>
        <Text style={[styles.title, isDarkMode && styles.textLight]}>健康排行榜</Text>
        <Text style={[styles.subtitle, isDarkMode && styles.textLightSecondary]}>
          查看你在好友圈中的排名
        </Text>
      </View>
      
      {/* 标签栏 */}
      {renderTabs()}
      
      {/* 时间范围选择 */}
      {renderTimeRanges()}
      
      {/* 我的排名卡片 */}
      <View style={[
        styles.myRankCard,
        isDarkMode ? styles.cardDark : styles.cardLight
      ]}>
        <View style={styles.myRankHeader}>
          <Text style={[styles.myRankTitle, isDarkMode && styles.textLight]}>
            我的{activeTab}排名
          </Text>
          <Text style={[styles.myRankTimeRange, isDarkMode && styles.textLightSecondary]}>
            本{timeRange}
          </Text>
        </View>
        
        <View style={styles.myRankContent}>
          <View style={styles.myRankPosition}>
            <Text style={styles.myRankNumber}>
              {currentUserRank[activeTab].rank}
            </Text>
            <Text style={[styles.myRankLabel, isDarkMode && styles.textLightSecondary]}>
              当前排名
            </Text>
            {renderRankChange(currentUserRank[activeTab].change)}
          </View>
          
          <View style={styles.myRankDivider} />
          
          <View style={styles.myRankScore}>
            <Text style={[styles.myScoreNumber, isDarkMode && styles.textLight]}>
              {currentUserRank[activeTab].score}
            </Text>
            <Text style={[styles.myScoreLabel, isDarkMode && styles.textLightSecondary]}>
              健康得分
            </Text>
          </View>
        </View>
      </View>
      
      {/* 排行榜列表 */}
      <View style={styles.rankingsContainer}>
        <View style={styles.rankingsHeader}>
          <Text style={[styles.rankingsTitle, isDarkMode && styles.textLight]}>
            好友{activeTab}排行
          </Text>
          <TouchableOpacity style={styles.refreshButton}>
            <Ionicons 
              name="refresh" 
              size={16} 
              color={isDarkMode ? "#ddd" : "#666"} 
              style={loading ? styles.refreshIconSpinning : {}}
            />
          </TouchableOpacity>
        </View>
        
        <FlatList
          data={sortedRankings}
          renderItem={renderRankItem}
          keyExtractor={(item) => item.id}
          showsVerticalScrollIndicator={false}
          style={styles.rankingsList}
          contentContainerStyle={styles.rankingsListContent}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  containerLight: {
    backgroundColor: '#f5f5f5',
  },
  containerDark: {
    backgroundColor: '#121212',
  },
  textLight: {
    color: '#f5f5f5',
  },
  textLightSecondary: {
    color: '#aaa',
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  tabsContainer: {
    marginBottom: 16,
  },
  tab: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#eee',
  },
  activeTab: {
    backgroundColor: '#4CAF50',
  },
  activeTabDark: {
    backgroundColor: '#2e7d32',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  activeTabText: {
    color: 'white',
    fontWeight: 'bold',
  },
  activeTabTextDark: {
    color: 'white',
  },
  timeRangesContainer: {
    flexDirection: 'row',
    marginBottom: 20,
    backgroundColor: isDarkMode => isDarkMode ? '#2a2a2a' : '#eee',
    borderRadius: 20,
    padding: 4,
  },
  timeRange: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    borderRadius: 16,
  },
  activeTimeRange: {
    backgroundColor: '#fff',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 1,
  },
  activeTimeRangeDark: {
    backgroundColor: '#444',
  },
  timeRangeText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  activeTimeRangeText: {
    color: '#4CAF50',
    fontWeight: 'bold',
  },
  activeTimeRangeTextDark: {
    color: '#81C784',
  },
  myRankCard: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 20,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  cardLight: {
    backgroundColor: '#fff',
  },
  cardDark: {
    backgroundColor: '#2a2a2a',
  },
  myRankHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  myRankTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  myRankTimeRange: {
    fontSize: 14,
    color: '#777',
  },
  myRankContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  myRankPosition: {
    flex: 1,
    alignItems: 'center',
  },
  myRankNumber: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#4CAF50',
    marginBottom: 4,
  },
  myRankLabel: {
    fontSize: 12,
    color: '#777',
    marginBottom: 8,
  },
  myRankDivider: {
    width: 1,
    height: 40,
    backgroundColor: '#eee',
    marginHorizontal: 16,
  },
  myRankScore: {
    flex: 1,
    alignItems: 'center',
  },
  myScoreNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  myScoreLabel: {
    fontSize: 12,
    color: '#777',
  },
  rankingsContainer: {
    flex: 1,
  },
  rankingsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  rankingsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  refreshButton: {
    padding: 4,
  },
  refreshIconSpinning: {
    transform: [{ rotate: '45deg' }],
  },
  rankingsList: {
    flex: 1,
  },
  rankingsListContent: {
    paddingBottom: 16,
  },
  rankItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 8,
    borderRadius: 12,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 1,
  },
  rankItemLight: {
    backgroundColor: '#fff',
  },
  rankItemDark: {
    backgroundColor: '#2a2a2a',
  },
  currentUserLight: {
    backgroundColor: '#e8f5e9',
    borderWidth: 1,
    borderColor: '#a5d6a7',
  },
  currentUserDark: {
    backgroundColor: '#1b5e20',
    borderWidth: 1,
    borderColor: '#2e7d32',
  },
  rankLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rankNumberContainer: {
    width: 28,
    alignItems: 'center',
    marginRight: 12,
  },
  rankNumber: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#666',
  },
  top1RankText: {
    color: '#f59e0b',
    fontSize: 16,
  },
  top2RankText: {
    color: '#94a3b8',
    fontSize: 15,
  },
  top3RankText: {
    color: '#c2410c',
    fontSize: 15,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    marginRight: 12,
  },
  userName: {
    fontSize: 15,
    fontWeight: '500',
    color: '#333',
  },
  currentUserText: {
    fontWeight: 'bold',
  },
  rankRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  scoreText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginRight: 8,
  },
  rankChangeUp: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  rankChangeDown: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  rankChangeSame: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(156, 163, 175, 0.1)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  rankChangeTextUp: {
    fontSize: 11,
    fontWeight: '500',
    color: '#10b981',
    marginLeft: 2,
  },
  rankChangeTextDown: {
    fontSize: 11,
    fontWeight: '500',
    color: '#ef4444',
    marginLeft: 2,
  },
  rankChangeTextSame: {
    fontSize: 11,
    fontWeight: '500',
    color: '#9ca3af',
    marginLeft: 2,
  },
});

export default RankingsScreen; 